import type { CreateTarget } from 'actions';
import type { Received } from 'validation';
import { EditModel } from 'edit/EditModel';
import { waitDialog } from 'dialogs';
import { getRecurrenceDefinition, type RecurrenceDefinition } from 'api/recurrence_definitions/endpoints/getRecurrenceDefinition';
import { postRecurrenceDefinition } from 'api/recurrence_definitions/endpoints/postRecurrenceDefinition';
import { putRecurrenceDefinition } from 'api/recurrence_definitions/endpoints/putRecurrenceDefinition';
import { EditableRecurrenceDefinition } from './EditableRecurrenceDefinition';
import { EditRecurrenceDefinitionDialog } from './EditRecurrenceDefinitionDialog';

/**
 * Arguments for `showEditRecurrenceDefinition`. Discriminated by `mode`:
 * 'create' opens a blank form; 'edit' loads the existing record
 * identified by `zoneName` + `name`; 'duplicate' pre-fills from the
 * source record but saves as a new record with a cleared name.
 */
export type ShowEditRecurrenceDefinitionArgs =
  | { readonly mode: 'create'; readonly createIn?: CreateTarget }
  | { readonly mode: 'edit'; readonly zoneName: string; readonly name: string }
  | {
      readonly mode: 'duplicate';
      readonly zoneName: string;
      readonly name: string;
    };

// Persist the entity: POST when it's a new record, PUT (addressed by the
// pre-edit identity) when editing an existing one. Returns the saved record so
// the EditModel can reinitialize the entity from it.
const save = (entity: EditableRecurrenceDefinition): Promise<Received<RecurrenceDefinition>> => {
  const body = entity.toSave();
  const original = entity.original;
  return original === null ? postRecurrenceDefinition(body) : putRecurrenceDefinition({ zone_name: original.zoneName, name: original.name }, body);
};

/**
 * Open the recurrence-definition edit dialog and wait for the user to save or
 * cancel. Resolves to `true` if a save succeeded (caller should refresh
 * whatever list view it owns), `undefined` if cancelled.
 *
 * Wraps the EditModel construction + dialog mounting; deliberately excludes any
 * "what happens after save" logic so the same dialog can be opened from
 * multiple places and each call site decides how to react.
 */
export const showEditRecurrenceDefinition = (args: ShowEditRecurrenceDefinitionArgs): Promise<true | undefined> => {
  // The entity is built from the loaded record, or `null` for a blank create
  // form (there is no server record to load yet).
  //
  // NOTE: annotate this as `Promise<RecurrenceDefinition | null>` and keep the
  // collapse — without it, `async () => null` infers `Promise<any>` under
  // `strictNullChecks: false`, the `any` absorbs the `Received` union, and
  // handing the raw envelope to the entity compiles cleanly.
  const initialize: () => Promise<RecurrenceDefinition | null> =
    args.mode === 'create'
      ? async () => null
      : async () => {
          const answer = await getRecurrenceDefinition(args.zoneName, args.name);
          // Rendered by EditDialog as "Failed to load <entity>: <message>", so each
          // sentence continues that one.
          if (answer.result === 'unreadable') throw new Error("the server's response could not be read. Please report this.");
          if (answer.result === 'degraded')
            throw new Error("part of the server's response could not be read, so saving it could overwrite that part. Please report this.");
          return answer.value;
        };

  const successMessage = args.mode === 'edit' ? 'Recurrence definition updated' : 'Recurrence definition created';

  const model = new EditModel(
    initialize,
    save,
    (loaded) =>
      new EditableRecurrenceDefinition(loaded, {
        duplicate: args.mode === 'duplicate',
        createIn: args.mode === 'create' ? args.createIn : undefined,
      }),
    {
      initialMode: args.mode === 'edit' ? 'edit' : 'create',
      successMessage,
    }
  );

  return waitDialog<true>((ok, cancel) => <EditRecurrenceDefinitionDialog model={model} onSaved={() => ok(true)} onClose={cancel} />);
};
